import React from "react";
import BlogTemplate from "./BlogTemplate";

// Import your images here (update paths as needed)
import ReactiveLoggingThumb from "../../assets/images/Blogs/Thumbnails/reactive-logging.jpg";
import ProcessStepsImage from "../../assets/images/Blogs/ReactiveLogging/process_steps.png";
import ZipFileImage from "../../assets/images/Blogs/ReactiveLogging/zip_file.png";
import ScenariosImage from "../../assets/images/Blogs/ReactiveLogging/scenarios.png";
import PreferencesImage from "../../assets/images/Blogs/ReactiveLogging/preferences.png";

const ReactiveLogging = () => {
  return (
    <BlogTemplate
      bannerTitle="Reactive Logging in Active Workspace"
    >
      <section className="container content-container">
        <div className="text-container">
          <p>
            Reactive logging in Active Workspace enables regular users to generate the log reactively in response to the issue/problem they observe while performing a specific business use case. This log can be collected by itself and sent to the administrator, or the administrator can collect it directly and diagnose the issue.
          </p>
          <p>
            Collect required logs in the context of the user and the business use case faster without any downtime. Generating a log from Active Workspace is very specific to that user session and hence has no impact on other users. 
          </p>
          <p>
            Reactive logging eliminates the need of setting variables through <code>tc_profilevars</code> and subsequent restart of the server that is required to enforce the logging variable.
          </p>
        </div>
        <div className="image-container">
           <img src={ReactiveLoggingThumb} alt="Reactive Logging in Active Workspace" />
        </div>
      </section>

      <section className="blog-section" style={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
        <h2 className="sub-title">Steps to Collect Logs</h2>
        <p>Generating logs from the Active Workspace client is a simple four-step process for regular users to follow:</p>
        <ul className="blog-list">
          <li>Starting logging with the required log options.</li>
          <li>Performing the use case that is exhibiting the issue.</li>
          <li>Stopping logging.</li>
          <li>Collecting the logs (such as Syslog, JNL, PJL, etc.) bundled in a zip file.</li>
        </ul>
        <div className="image-container" style={{ margin: "2rem auto", width: "100%", maxWidth: "800px" }}>
           <img src={ProcessStepsImage} alt="Start logging, reproduce issue, stop logging, collect logs" />
        </div>
        <div className="image-container" style={{ margin: "1rem auto", width: "100%", maxWidth: "600px" }}>
           <img src={ZipFileImage} alt="Reactive logs zip file generation message" />
        </div>
      </section>

      <section className="blog-section">
        <h2 className="sub-title">Business Scenarios and Preferences</h2>
        <p>You can enable reactive logging for various business scenarios suiting your business needs by configuring specific preferences.</p>
        
        <div className="image-container" style={{ margin: "2rem auto", width: "100%", maxWidth: "800px" }}>
           <img src={ScenariosImage} alt="Display log file name, allow user to download, or display machine name" />
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "2rem", marginBottom: "2rem", fontFamily: "'Poppins', sans-serif" }}>
          <thead>
            <tr style={{ backgroundColor: "#0b4a7d", color: "white", textAlign: "left" }}>
              <th style={{ padding: "12px", border: "1px solid #ddd" }}>Scenarios</th>
              <th style={{ padding: "12px", border: "1px solid #ddd" }}>Preferences</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}><strong>Scenario 1:</strong> Display log file name and send email to admin</td>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>
                <code>TC_reactive_logging_notification_list: admin@genesis.com</code><br/>
                <code>TC_reactive_logging_file_download: false</code>
              </td>
            </tr>
            <tr style={{ backgroundColor: "#f4f4f4" }}>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}><strong>Scenario 2:</strong> Allow the user to download the file and send an email to the admin</td>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>
                <code>TC_reactive_logging_notification_list: admin@genesis.com</code><br/>
                <code>TC_reactive_logging_file_download: true</code>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}><strong>Scenario 3:</strong> Display log file and machine name</td>
              <td style={{ padding: "12px", border: "1px solid #ddd" }}>
                <code>TC_reactive_logging_notification_list: Don’t set this</code><br/>
                <code>TC_reactive_logging_file_download: false</code>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="image-container" style={{ margin: "2rem auto", width: "100%", maxWidth: "600px" }}>
           <img src={PreferencesImage} alt="Teamcenter Preferences for Reactive Logging" />
        </div>

        <blockquote style={{ padding: "1rem", backgroundColor: "#fff3cd", borderLeft: "5px solid #ffc107", margin: "2rem 0" }}>
          <strong>Note:</strong> <code>TC_reactive_logging_file_delete</code> preference is to decide whether to delete the zipped log files in the Transient volume after reading it.
        </blockquote>

        <p className="text-bold">
          In summary, Reactive Logging brings a lot of value to business by enabling regular users to react to the issues they observe faster. Thereby, enabling the administrator to collect user and business use case-centric respective logs without any downtime at reduced time and effort.
        </p>
      </section>
    </BlogTemplate>
  );
};

export default ReactiveLogging;
